import React, { useMemo } from 'react';

import { Modal } from 'src/features/onboarding/components/shared/modal/Modal';
import {
  useFeedbackModalStore,
  FEEDBACK_MAX_LENGTH,
} from 'src/store/feedback-modal';
import { useAuthStore } from 'src/store/auth';
import { Button } from 'src/components/ui/button';
import { Textarea } from 'src/components/ui/textarea';
import './FeedbackModal.scss';

const REVIEW_URL =
  'https://www.trustpilot.com/review/onehealthplus.com';

const RATING_OPTIONS = [
  { value: 1, label: 'Poor', icon: 'poor' },
  { value: 2, label: 'Fair', icon: 'fair' },
  { value: 3, label: 'Okay', icon: 'ok' },
  { value: 4, label: 'Good', icon: 'good' },
  { value: 5, label: 'Excellent', icon: 'excellent' },
] as const;

const useFirstName = () => {
  const { user } = useAuthStore();
  return user?.firstName || '';
};

const RatingView: React.FC = () => {
  const {
    rating,
    feedbackText,
    setRating,
    setFeedbackText,
    submitRating,
    isSubmitting,
  } = useFeedbackModalStore();

  const showTextarea = rating > 0 && rating < 5;

  return (
    <div className="feedback-modal__body">
      <h2 className="feedback-modal__title">How did we do?</h2>
      <p className="feedback-modal__subtitle">
        Now that you&apos;re all set, we&apos;d love your quick
        feedback on your experience.
      </p>
      <p className="feedback-modal__subtitle">
        It only takes a few seconds, and it helps us make OneHealth Plus
        better every day.
      </p>
      <div className="feedback-modal__faces">
        {RATING_OPTIONS.map((option) => {
          const isSelected = rating === option.value;
          const suffix = isSelected ? '_on' : '_off';
          return (
            <button
              key={option.value}
              type="button"
              className="feedback-modal__face-btn"
              onClick={() => setRating(option.value)}
              aria-label={option.label}
            >
              <img
                src={`/icons/feedback/${option.icon}${suffix}.svg`}
                alt={option.label}
                className="feedback-modal__face-icon"
              />
              <span
                className={`feedback-modal__face-label${isSelected ? ' feedback-modal__face-label--active' : ''}`}
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
      {showTextarea && (
        <div className="w-full">
          <Textarea
            placeholder="Tell us more about your experience"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            maxLength={FEEDBACK_MAX_LENGTH}
            className="min-h-[120px]"
          />
        </div>
      )}
      <Button
        className="feedback-modal__submit-btn"
        disabled={rating === 0 || isSubmitting}
        onClick={submitRating}
      >
        {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
      </Button>
    </div>
  );
};

const ExcellentView: React.FC = () => {
  const { close } = useFeedbackModalStore();
  const firstName = useFirstName();

  return (
    <div className="feedback-modal__body">
      <img
        src="/icons/feedback/claphand.svg"
        alt="clap"
        className="feedback-modal__claphand"
      />
      <h2 className="feedback-modal__title">
        Thanks, {firstName || 'friend'}
      </h2>
      <p className="feedback-modal__subtitle">
        We appreciate the feedback. It means a lot. Would you mind
        taking 30 seconds to share your experience? It helps other
        people like you find us.
      </p>
      <img
        src="/icons/feedback/stars.png"
        alt="5 stars"
        className="h-10 mt-2"
      />
      <a
        href={REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button className="feedback-modal__submit-btn">
          LEAVE A REVIEW
        </Button>
      </a>
      <button
        type="button"
        className="feedback-modal__maybe-later"
        onClick={close}
      >
        NOT RIGHT NOW
      </button>
    </div>
  );
};

const FeedbackThanksView: React.FC = () => {
  const { close } = useFeedbackModalStore();
  const firstName = useFirstName();

  return (
    <div className="feedback-modal__body">
      <h2 className="feedback-modal__title">
        Thanks, {firstName || 'friend'}!
      </h2>
      <p className="feedback-modal__subtitle">
        We appreciate the feedback.
      </p>
      <img
        src="/icons/feedback/claphand.svg"
        alt="thanks"
        className="feedback-modal__claphand"
      />
      <Button className="feedback-modal__submit-btn" onClick={close}>
        CLOSE
      </Button>
    </div>
  );
};

export const FeedbackModal: React.FC = () => {
  const { isOpen, close, view } = useFeedbackModalStore();

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      className="feedback-modal"
      blockOverlayClick
    >
      {view === 'rating' && <RatingView />}
      {view === 'excellent' && <ExcellentView />}
      {view === 'feedback-thanks' && <FeedbackThanksView />}
    </Modal>
  );
};
